import React, { useRef, useState } from "react";
import styled from "styled-components";
import Cover from "./assets/password-cover.svg";

/**
 * 各科開始前的說明頁（Test information）。
 *
 * 影片可以是 YouTube 網址，也可以是教務上傳的 mp4——後台兩種都能填，
 * 所以這裡兩種都要能播。
 *
 * Confirm 要等說明影片播完才會亮起來——這是刻意的，說明影片就是要看完的。
 * 而且不能用拖進度條跳過：只允許往回看，不允許快轉超過看過的位置。
 *
 * 但有四種情況不要求看完，那時就退回勾選確認，不能讓學生卡在這一頁：
 *   1. 這一科沒有設定影片
 *   2. 影片載不出來（網路問題、檔案被刪）
 *   3. 用的是 YouTube 內嵌——跨網域，抓不到播放結束的事件
 *   4. 續考——影片在第一次進場時就看過了，再看一次只是折磨人
 */

const StyledInfo = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  z-index: 1;
  height: 100vh;
  width: 100%;
  overflow-y: auto;
  background-image: url(${Cover});
  background-size: 100%;
  background-repeat: repeat-y;
  font-family: Arial, "Noto Sans TC", sans-serif;

  .inner {
    max-width: 820px;
    margin: 0 auto;
    padding: 5vh 24px 8vh;
  }

  .sheet {
    background: rgba(255, 255, 255, 0.97);
    color: #1d2430;
    border-radius: 10px;
    padding: 28px 32px 32px;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.2);
    /* 考試站全站是 .App { text-align: center }，會把條列說明拉成置中，
       項目符號在左、文字在中間很難讀。這裡改回左對齊，只有標題置中。 */
    text-align: left;
  }

  h1 {
    font-size: 24px;
    margin: 0 0 4px;
    text-align: center;
  }
  .sub {
    color: #5b6774;
    font-size: 15px;
    margin: 0 0 22px;
    text-align: center;
  }

  .video {
    width: 100%;
    aspect-ratio: 16 / 9;
    background: #000;
    border-radius: 8px;
    overflow: hidden;
    margin-bottom: 20px;
    iframe,
    video {
      width: 100%;
      height: 100%;
      border: 0;
      display: block;
    }
  }
  /* aspect-ratio 在舊瀏覽器沒有支援，退回固定比例的內距寫法 */
  @supports not (aspect-ratio: 16 / 9) {
    .video {
      height: 0;
      padding-bottom: 56.25%;
      position: relative;
      iframe,
      video {
        position: absolute;
        top: 0;
        left: 0;
        height: 100%;
      }
    }
  }

  .resume-note {
    background: #eef6f1;
    border-left: 4px solid #2f5d50;
    border-radius: 8px;
    padding: 14px 16px;
    margin-bottom: 20px;
    color: #2f5d50;
    font-size: 15px;
    line-height: 1.8;
  }

  .no-video {
    background: #f6f7f9;
    border: 1px dashed #cfd6dd;
    border-radius: 8px;
    padding: 18px;
    color: #5b6774;
    font-size: 14px;
    margin-bottom: 20px;
  }

  ul.rules {
    margin: 0 0 22px;
    padding-left: 20px;
    line-height: 2;
    font-size: 15px;
  }

  .ack {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    background: #fdf6ea;
    border-radius: 8px;
    padding: 14px 16px;
    margin-bottom: 22px;
    font-size: 15px;
    cursor: pointer;
    input {
      margin-top: 4px;
      width: 18px;
      height: 18px;
      cursor: pointer;
    }
  }

  /* 影片版沒有勾選框，只是告訴考生還要看多久 */
  .ack--video {
    cursor: default;
    color: #6b5a45;
    border-left: 4px solid #efc180;

    b {
      color: #a8432a;
    }

    &.is-done {
      background: #eef6f1;
      border-left-color: #2f5d50;
      color: #2f5d50;
      font-weight: 600;
    }
  }

  .actions {
    display: flex;
    gap: 12px;
    align-items: center;
    flex-wrap: wrap;
  }

  button.confirm {
    background: #e2704e;
    color: #fff;
    border: 0;
    border-radius: 6px;
    padding: 13px 34px;
    font-size: 16px;
    font-weight: 700;
    cursor: pointer;
    &:disabled {
      background: #cfd6dd;
      cursor: not-allowed;
    }
  }
  button.back {
    background: none;
    border: 1px solid #cfd6dd;
    border-radius: 6px;
    padding: 12px 22px;
    font-size: 15px;
    cursor: pointer;
  }

  .warn {
    margin-top: 18px;
    font-size: 14px;
    color: #a8432a;
    line-height: 1.8;
  }
`;

const isYouTube = (url) => /youtube\.com|youtu\.be/.test(url || "");

const TestInformation = ({ section, mode, onBack, onConfirm, busy }) => {
  // 續考＝上次被中斷、伺服器留著作答進度
  const resuming = !!section.resuming;
  const [watched, setWatched] = useState(false);
  const [acked, setAcked] = useState(false);
  const [videoFailed, setVideoFailed] = useState(false);
  const [remaining, setRemaining] = useState(null);
  // 看到哪裡了——用來擋快轉。ref 而不是 state：每秒更新好幾次，
  // 放 state 會讓整頁重畫。
  const maxWatched = useRef(0);
  const videoRef = useRef(null);

  const hasVideo = !!section.videoUrl;
  const isFile = hasVideo && !isYouTube(section.videoUrl);
  // 只有「可播放的影片檔」才用播完當條件；其餘退回勾選
  const gateOnVideo = isFile && !videoFailed && !resuming;
  const canConfirm = (gateOnVideo ? watched : acked) && !busy;
  const formal = mode !== "practice";

  const handleTimeUpdate = (e) => {
    const v = e.target;
    if (v.currentTime > maxWatched.current) {
      maxWatched.current = v.currentTime;
    }
    if (v.duration) {
      setRemaining(Math.max(0, Math.ceil(v.duration - v.currentTime)));
    }
  };

  /** 不准快轉——往回看可以，往前拉超過看過的地方就拉回去 */
  const handleSeeking = (e) => {
    const v = e.target;
    if (v.currentTime > maxWatched.current + 0.5) {
      v.currentTime = maxWatched.current;
    }
  };

  return (
    <StyledInfo>
      <div className="inner">
        <div className="sheet">
          <h1>{section.name}</h1>
          <p className="sub">
            {resuming
              ? "繼續作答 ｜ 會回到中斷前的進度"
              : "Test information ｜ 開始前請詳閱以下說明"}
          </p>

          {resuming && (
            <div className="resume-note">
              這一科先前被中斷過。按下「Confirm」之後會回到中斷前的那一頁，
              已經寫下的答案都還在，計時器也接著剩下的時間繼續跑。
            </div>
          )}

          {section.videoUrl ? (
            <div className="video">
              {isYouTube(section.videoUrl) ? (
                <iframe
                  src={section.videoUrl}
                  title="test information"
                  allow="accelerometer; encrypted-media; picture-in-picture"
                  allowFullScreen
                />
              ) : (
                <video
                  ref={videoRef}
                  src={section.videoUrl}
                  controls
                  controlsList="nodownload noplaybackrate"
                  disablePictureInPicture
                  onTimeUpdate={handleTimeUpdate}
                  onSeeking={handleSeeking}
                  onEnded={() => {
                    setWatched(true);
                    setRemaining(0);
                  }}
                  onError={() => setVideoFailed(true)}
                />
              )}
            </div>
          ) : (
            <div className="no-video">
              這一科目前沒有設定說明影片，請詳閱下方文字說明。
            </div>
          )}

          <ul className="rules">
            <li>
              題數 {section.questions} 題，作答時間 {section.minutes} 分鐘
              {resuming ? "（這次接著剩下的時間繼續）" : ""}。
            </li>
            {section.note && <li>{section.note}</li>}
            <li>時間到系統會自動收卷，請留意畫面上方的計時器。</li>
            {formal ? (
              <li style={{ color: "#a8432a", fontWeight: 600 }}>
                作答期間請勿切換分頁或關閉視窗——正式模考一旦離開頁面，
                系統會中斷這一科，必須由教務人員解鎖才能繼續。
              </li>
            ) : (
              <li>自我練習可以中途離開，回來後從原本的地方繼續。</li>
            )}
          </ul>

          {gateOnVideo ? (
            <div className={`ack ack--video ${watched ? "is-done" : ""}`}>
              {watched ? (
                <span>✓ 說明影片已看完，可以開始測驗了。</span>
              ) : (
                <span>
                  請先看完上方的說明影片，看完之後才能開始測驗。
                  {remaining !== null && remaining > 0 && (
                    <b>（還有 {remaining} 秒）</b>
                  )}
                </span>
              )}
            </div>
          ) : (
            <label className="ack">
              <input
                type="checkbox"
                checked={acked}
                onChange={(e) => setAcked(e.target.checked)}
              />
              <span>
                {videoFailed
                  ? "說明影片載入失敗。我已閱讀上方文字說明，準備好開始這一科的測驗。"
                  : "我已閱讀並了解以上說明，準備好開始這一科的測驗。"}
              </span>
            </label>
          )}

          <div className="actions">
            <button className="back" onClick={onBack} disabled={busy}>
              返回
            </button>
            <button className="confirm" onClick={onConfirm} disabled={!canConfirm}>
              {busy ? "進入中…" : "Confirm 開始測驗"}
            </button>
          </div>

          {formal && (
            <p className="warn">
              按下「Confirm」後計時立刻開始，中途無法暫停。
              請先確認網路穩定、耳機可用，並把其他分頁關閉。
            </p>
          )}
        </div>
      </div>
    </StyledInfo>
  );
};

export default TestInformation;
